import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { LocalizedMessage } from '@services/requests/localizedMessagesService/interfaces';
import { ContainersData } from '@services/requests/searchLoaders/containersLoader/ContainersLoaderQuery';
import { TrucksData } from '@services/requests/searchLoaders/trucksLoader/TrucksLoaderQuery';
import React from 'react';

import { checkEmptyInputValue } from '../../../helpers';
import useDelivery from '../../../useDelivery';
import { FclFtlTransport } from './types';

interface TransportTypeItemProps {
  transportTypeIndex: number;
  isDeletable: boolean;
  mainLanguage: string;
  transportType: FclFtlTransport;
  transportList: TrucksData[] | ContainersData[];
  cargoTitle: string;
  transportTypeTitle: string;
  transportWeightTitle: string;
  entityTypeHint: React.ReactNode;
}

function TransportTypeItem({
  cargoTitle,
  isDeletable,
  transportType,
  transportList,
  transportTypeTitle,
  entityTypeHint,
  transportTypeIndex,
  mainLanguage,
  transportWeightTitle,
}: TransportTypeItemProps) {
  const { useRouteParams, useIsRoutesLoading, useIsLoading } = useDelivery();
  const setRouteParams = useRouteParams().setState;
  const [isRoutesLoading] = useIsRoutesLoading().useState();
  const [isLoading] = useIsLoading().useState();

  const isWeightInputInvalid = checkEmptyInputValue(transportType.weight);

  /**
   * Обработка изменения значения типа траспорта для fcl ftl перевозки
   * @param key
   * @param value
   * @param transportTypeIndex
   */
  const changeTransportTypeValueHandler = <T extends keyof FclFtlTransport>(
    key: T,
    value: FclFtlTransport[T]
  ) => {
    setRouteParams((prevState) => {
      const type = transportType.type;
      const newTransportTypes = prevState.customCargoParameters.transportTypes.slice();

      let occurrence = -1;
      let actualIndex = -1;

      for (let i = 0; i < newTransportTypes.length; i++) {
        if (newTransportTypes[i].type === type) {
          occurrence++;
          if (occurrence === transportTypeIndex) {
            actualIndex = i;
            break;
          }
        }
      }

      if (actualIndex === -1) {
        return prevState;
      }

      const newTransportTypeState = { ...newTransportTypes[actualIndex], [key]: value };
      newTransportTypes.splice(actualIndex, 1, newTransportTypeState);

      return {
        ...prevState,
        customCargoParameters: {
          ...prevState.customCargoParameters,
          transportTypes: newTransportTypes,
        },
      };
    });
  };

  /**
   * Удаление типа траспорта для fcl ftl перевозки
   * @param index
   */
  const deleteTransportTypeHandler = () => {
    setRouteParams((prevState) => {
      const type = transportType.type;
      let occurrence = -1;
      let actualIndex = -1;

      for (let i = 0; i < prevState.customCargoParameters.transportTypes.length; i++) {
        if (prevState.customCargoParameters.transportTypes[i].type === type) {
          occurrence++;
          if (occurrence === transportTypeIndex) {
            actualIndex = i;
            break;
          }
        }
      }

      if (actualIndex === -1) {
        return prevState;
      }

      return {
        ...prevState,
        customCargoParameters: {
          ...prevState.customCargoParameters,
          transportTypes: prevState.customCargoParameters.transportTypes.filter(
            (_, i) => i !== actualIndex
          ),
        },
      };
    });
  };

  /**
   * Получение локализованного названия
   * @param defaultName
   * @param localizations
   */
  const generateLocalizedNameHandler = (
    defaultName: string,
    localizations: LocalizedMessage[]
  ): string => {
    const localization = localizations.find((l) => l.lang_id === mainLanguage);
    if (!localization || localization.message.length === 0) {
      return defaultName;
    }

    return localization.message;
  };

  return (
    <div className="cargo-item">
      <Box sx={{ position: 'relative' }}>
        <Typography
          variant={'subtitle2'}
          color={'grey[700]'}
          marginBottom={'15px'}
          sx={{
            mb: {
              sx: 15,
              md: 18,
              lg: 15,
            },
          }}
          textTransform={'capitalize'}
        >
          {cargoTitle}
        </Typography>
        <Box className={'cargo-inputs-block'}>
          <Box width={'100%'}>
            <TextField
              fullWidth
              variant={'standard'}
              value={transportType?.transportTypeId.toString() ?? ''}
              select
              sx={{
                '& .MuiInput-input': {
                  color: '#383838!important',
                  fontStyle: 'normal!important',
                  fontWeight: '600!important',
                  lineHeight: '22px!important',
                  textAlign: 'right!important',
                  fontSize: '12px!important',
                },
              }}
              disabled={transportList.length === 0 || isRoutesLoading || isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ margin: '0' }}>
                    <Typography
                      sx={{ color: 'grey[700]', position: 'absolute', pointerEvents: 'none' }}
                      component={'span'}
                      variant={'h5'}
                      className={'cargo-inputs-block__text-field'}
                    >
                      {transportTypeTitle}
                      <Tooltip
                        title={entityTypeHint}
                        enterTouchDelay={0}
                        leaveTouchDelay={10000}
                        arrow
                      >
                        <span className={'cargo-inputs-block__tooltip'}>?</span>
                      </Tooltip>
                    </Typography>
                  </InputAdornment>
                ),
              }}
              onChange={(event) =>
                changeTransportTypeValueHandler('transportTypeId', parseInt(event.target.value))
              }
            >
              {transportList.map((option: TrucksData | ContainersData) => (
                <MenuItem value={option.id} key={option.id}>
                  {generateLocalizedNameHandler(option.default_name, option.localized_names_data)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            error={isWeightInputInvalid}
            focused={isWeightInputInvalid}
            fullWidth
            className="transport-type-quantity"
            variant={'standard'}
            value={transportType.weight}
            type={'number'}
            disabled={isRoutesLoading || isLoading}
            InputProps={{
              inputMode: 'numeric',
              startAdornment: (
                <InputAdornment position="start">
                  <Typography
                    sx={{ color: 'grey[700]', position: 'absolute', pointerEvents: 'none' }}
                    component={'span'}
                    variant={'h5'}
                  >
                    {transportWeightTitle}
                  </Typography>
                </InputAdornment>
              ),
            }}
            onFocus={() => changeTransportTypeValueHandler('weight', parseFloat(''))}
            onChange={(event) =>
              changeTransportTypeValueHandler('weight', parseFloat(event.target.value))
            }
          />
        </Box>
        {isDeletable && (
          <IconButton
            sx={{
              position: 'absolute',
              right: '-15px',
              top: '-15px',
            }}
            disabled={isRoutesLoading || isLoading}
            size={'small'}
            onClick={() => deleteTransportTypeHandler()}
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>
        )}
      </Box>
    </div>
  );
}

export { TransportTypeItem };
